/**
 * The one player picture every player surface renders (ISSUE-983 R2): the
 * injury lists, the player page header, and every later lineup, leaders and
 * box score row. No second headshot component exists anywhere in the app;
 * PlayerHeadshot.test.tsx fails the build if one appears.
 *
 * The URL is only ever a media URL from the server, never a vendor URL
 * (ISSUE-983 R4). A missing URL, or an image that fails to load, shows the
 * silhouette, so a broken image never reaches a fan. A caller may pass
 * fallbackInitials; then a missing or broken image shows those initials
 * instead of the silhouette (ISSUE-1164).
 *
 * Every caller prints the player's name beside the picture, so the picture is
 * decoration and hidden from assistive technology.
 */
import { cn } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

export type typePlayerHeadshotSize = "sm" | "md" | "lg" | "xl";

export type typePlayerHeadshotProps = {
  /** A media URL from the server, or null or absent for the silhouette. */
  headshotUrl?: string | null;
  /** sm 24 px (box score), md 40 px (lineup, leaders), lg 56 px (injuries), xl 72 px (player header). */
  size: typePlayerHeadshotSize;
  /** When set and not blank, a missing or broken image shows these initials instead of the silhouette (player-profile rule 5). The caller computes them. */
  fallbackInitials?: string | undefined;
};

const SIZE_CLASSES: Record<typePlayerHeadshotSize, string> = {
  sm: "h-6 w-6",
  md: "h-10 w-10",
  lg: "h-14 w-14",
  xl: "h-18 w-18",
};

const INITIALS_TEXT_CLASSES: Record<typePlayerHeadshotSize, string> = {
  sm: "text-small",
  md: "text-caption",
  lg: "text-h2",
  xl: "text-h1",
};

export const PlayerHeadshot = (props: typePlayerHeadshotProps): ReactNode => {
  const { headshotUrl, size, fallbackInitials } = props;
  const [hasFailed, setHasFailed] = useState(false);
  useEffect(() => {
    setHasFailed(false);
  }, [headshotUrl]);

  if (
    headshotUrl !== undefined &&
    headshotUrl !== null &&
    headshotUrl !== "" &&
    !hasFailed
  ) {
    return (
      <img
        src={headshotUrl}
        alt=""
        aria-hidden="true"
        loading="lazy"
        data-testid="player-headshot"
        className={cn(
          "shrink-0 rounded-full bg-surface-sunken object-cover",
          SIZE_CLASSES[size],
        )}
        onError={() => {
          setHasFailed(true);
        }}
      />
    );
  }

  if (fallbackInitials !== undefined && fallbackInitials.trim() !== "") {
    return (
      <span
        data-testid="player-headshot-initials"
        aria-hidden="true"
        className={cn(
          "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-ink-secondary font-bold text-surface",
          SIZE_CLASSES[size],
          INITIALS_TEXT_CLASSES[size],
        )}
      >
        {fallbackInitials}
      </span>
    );
  }

  return (
    <span
      data-testid="player-headshot-placeholder"
      aria-hidden="true"
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-sunken text-ink-tertiary",
        SIZE_CLASSES[size],
      )}
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="h-3/4 w-3/4 fill-current"
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7z" />
      </svg>
    </span>
  );
};
